/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { JobFinderListItem } from '@gh/builder-shared';
import { BuilderPermission, DrawInvoiceStatus, InvoiceType } from '@gh/builder-shared-data';
import {
  EnumDictionary,
  FormStateSnapshot,
  getParentRouteUrl,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { manualPrimitiveTracker, RouteDataService } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { WaitingService } from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { openInNewWindow } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { map$, skip$, switchMap$ } from '@gh/rx/operators';

import { includes, isEmpty, isNil, toNumber } from 'lodash';

import { JobFinancialsBusinessService } from '../../resources/job-financials-business.service';
import {
  buildJobFinancialsTradingAccount,
  EMPTY_JOB_FINANCIALS_TRADING_ACCOUNT,
  getEmptyJobFinancials,
  JobFinancials,
  JobFinancialsDraw,
  JobFinancialsDrawsFilterCode,
  JobFinancialsExpense,
  JobFinancialsExpensesFilterCode,
  JobFinancialsInfo,
  JobFinancialsTabs,
  JobFinancialsTradingAccount,
} from '../../resources/job-financials.model';

@Component({
  standalone: false,
  selector: 'gh-job-financials',
  templateUrl: 'job-financials.component.html',
})
export class JobFinancialsComponent implements OnInit {

  jobFinancials: Maybe<JobFinancials>;
  jobFinancialsDrawList: JobFinancialsDraw[];
  jobFinancialsDrawListHighlights: JobFinancialsDraw[];
  jobFinancialsExpenseList: JobFinancialsExpense[];
  jobFinancialsTradingAccount: JobFinancialsTradingAccount;
  originalJobFinancials: Maybe<JobFinancials>;
  invoiceTypes: EnumDictionary<InvoiceType>;
  invoiceStatuses: EnumDictionary<DrawInvoiceStatus>;
  drawsLayout: GridLayout;
  paymentsLayout: GridLayout;
  expensesLayout: GridLayout;
  jobFinancialsTabs = JobFinancialsTabs;
  selectedTab = JobFinancialsTabs.Draws;
  drawListFilter = JobFinancialsDrawsFilterCode.isOnlyActiveDraws;
  expensesListFilter = JobFinancialsExpensesFilterCode.isAllExpenses;
  jobTradingAccountReportUrl?: string;
  jobCostSummaryReportUrl?: string;
  jobTracker = manualPrimitiveTracker<Maybe<ID>>('jobId');

  constructor(private businessService: JobFinancialsBusinessService,
              private waitingService: WaitingService,
              private translationService: TranslationService,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: UserContext) {
  }

  get currentJobId(): Maybe<ID> {
    return this.jobFinancials && this.jobFinancials.jobInfo
      ? this.jobFinancials.jobInfo.value.jobId
      : void 0;
  }

  get currentJobInfo(): Maybe<FormStateSnapshot<JobFinancialsInfo>> {
    return this.jobFinancials && this.jobFinancials.jobInfo
      ? this.jobFinancials.jobInfo
      : void 0;
  }

  get isActive(): boolean {
    return !isNil(this.currentJobId);
  }

  get parentTitle(): string {
    return this.isBackRouterActive() && this.jobFinancials
      ? this.isJobDetailsBackRoute()
        ? `${this.translationService.get('lbl_job')} #: ${this.jobFinancials.jobInfo.value.jobNumber}`
        : this.translationService.get('lbl_jobs')
      : this.translationService.get('lbl_job_admin');
  }

  get isPrintVisible(): boolean {
    switch (this.selectedTab) {
      case JobFinancialsTabs.Draws:
      case JobFinancialsTabs.TradingAccount:
        return this.userContext.hasPermission(BuilderPermission.Reports.JobAdmin.JOB_TRADING_ACCOUNT);
      case JobFinancialsTabs.Expenses:
        return this.userContext.hasPermission(BuilderPermission.Reports.JobAdmin.JOB_COST_SUMMARY);
      default:
        return false;
    }
  }

  ngOnInit(): void {
    const {
      jobFinancials, drawsLayout, paymentsLayout, expensesLayout, invoiceTypes, invoiceStatuses,
      reportUrls: { jobTradingAccountReportUrl, jobCostSummaryReportUrl },
    } = getRouteInitData(this.route);
    this.refreshJobFinancials(jobFinancials);
    this.drawsLayout = drawsLayout;
    this.paymentsLayout = paymentsLayout;
    this.expensesLayout = expensesLayout;
    this.invoiceTypes = invoiceTypes;
    this.invoiceStatuses = invoiceStatuses;
    this.jobCostSummaryReportUrl = jobCostSummaryReportUrl;
    this.jobTradingAccountReportUrl = jobTradingAccountReportUrl;

    const tracker = this.routeData.tracker(this.jobTracker);
    tracker.listen(this.route);
    tracker.filterChanges.pipe(
      skip$(1),
      switchMap$(([jobId]) => jobId
        ? this.businessService.get(jobId).pipe(this.waitingService.operator())
        : of$(getEmptyJobFinancials())),
      switchMap$((financials: JobFinancials) =>
        financials.jobInfo && financials.jobInfo.value.jobId
          ? this.businessService.generateReportUrls(financials.jobInfo.value.jobId).pipe(
          (this.waitingService.operator()),
          map$((reportUrls) => ({ financials, ...reportUrls })))
          : of$(<any>{ financials, jobTradingAccountReportUrl: void 0, jobCostSummaryReportUrl: void 0 })))
      .subscribe(({ financials, jobTradingAccountReportUrl: jtaUrl, jobCostSummaryReportUrl: jcsUrl }) => {
        this.refreshJobFinancials(financials);
        this.jobTradingAccountReportUrl = jtaUrl;
        this.jobCostSummaryReportUrl = jcsUrl;
      });
  }

  onJobChange(job: Maybe<JobFinderListItem>): void {
    this.jobTracker.setValue(job ? job.id : void 0);
  }

  onDrawsFilterChange(code: JobFinancialsDrawsFilterCode): void {
    this.drawListFilter = code;
    this.jobFinancialsDrawListHighlights = this.getOriginalJobFinancialsDrawList()
      .filter(({ status }: JobFinancialsDraw) => {
        if (code === JobFinancialsDrawsFilterCode.isAllDraws) {
          return includes([DrawInvoiceStatus.Paid], status);
        }
      });
    this.jobFinancialsDrawList = this.getOriginalJobFinancialsDrawList()
      .filter(({ status }: JobFinancialsDraw) => {
        if (code === JobFinancialsDrawsFilterCode.isOnlyActiveDraws) {
          return includes([DrawInvoiceStatus.Authorized, DrawInvoiceStatus.Paid], status);
        } else if (code === JobFinancialsDrawsFilterCode.isAllDraws) {
          return true;
        }
      });
  }

  onExpensesFilterChange(code: JobFinancialsExpensesFilterCode): void {
    this.expensesListFilter = code;
    this.jobFinancialsExpenseList = this.getOriginalJobFinancialsExpenseList()
      .filter(({ actualCost, budgetCost }) => {
        switch (code) {
          case JobFinancialsExpensesFilterCode.isNonZeroExpenses:
            return !(toNumber(actualCost) === 0 && toNumber(budgetCost) === 0);
          case JobFinancialsExpensesFilterCode.isActualBudgetExpenses:
            return actualCost > budgetCost;
          case JobFinancialsExpensesFilterCode.isAllExpenses:
            return true;
          default:
            return true;
        }
      });
  }

  onJobPrint(): void {
    if (this.jobFinancials) {
      openInNewWindow({
        [JobFinancialsTabs.Draws]: this.jobTradingAccountReportUrl,
        [JobFinancialsTabs.Expenses]: this.jobCostSummaryReportUrl,
        [JobFinancialsTabs.TradingAccount]: this.jobTradingAccountReportUrl,
      }[this.selectedTab]!);
    }
  }

  onTabSelect(name: JobFinancialsTabs): void {
    this.selectedTab = name;
  }

  isBackRouterActive(): boolean {
    return !isEmpty(getParentRouteUrl(this.route));
  }

  isJobDetailsBackRoute(): boolean {
    // depends on route changing according to builder-job-admin.routing.module.ts
    return this.isBackRouterActive() && includes(getParentRouteUrl(this.route), 'job-admin/jobs/');
  }

  private refreshJobFinancials(jobFinancials: Maybe<JobFinancials>): void {
    this.jobFinancials = jobFinancials;
    this.jobFinancialsDrawList = this.jobFinancials ? this.jobFinancials.invoices : [];
    this.jobFinancialsExpenseList = this.jobFinancials ? this.jobFinancials.jobExpenses : [];
    this.jobFinancialsTradingAccount = this.jobFinancials
      ? buildJobFinancialsTradingAccount(this.jobFinancials)
      : EMPTY_JOB_FINANCIALS_TRADING_ACCOUNT;
    this.originalJobFinancials = jobFinancials;
    if (!isEmpty(this.originalJobFinancials)) {
      this.onDrawsFilterChange(JobFinancialsDrawsFilterCode.isOnlyActiveDraws);
      this.onExpensesFilterChange(JobFinancialsExpensesFilterCode.isAllExpenses);
    }
  }

  private getOriginalJobFinancialsDrawList(): JobFinancialsDraw[] {
    return this.originalJobFinancials
      ? this.originalJobFinancials.invoices
      : [];
  }

  private getOriginalJobFinancialsExpenseList(): JobFinancialsExpense[] {
    return this.originalJobFinancials
      ? this.originalJobFinancials.jobExpenses
      : [];
  }
}
